import importIcon from '@disy/cadenza-icons/import.svg';
import { assert } from '@disy/lib/custom-error';
import { logger } from '@disy/lib/logging';
import { delay } from '@disy/lib/promise-utils';
import { icon } from '@disy/ui/icon';

import { Modal } from 'ui/dialog/modal/modal';
import { ProgressBar } from 'ui/progress-bar/progress-bar';

import type { UploadedFileDto } from 'cadenza/api-client/dto/uploaded-file-dto';
import type { WorkOrderProgressEvent } from 'cadenza/api-client/dto/workorder-dto';
import type { SaveWorkbookDto } from 'cadenza/api-client/workbook-api';
import { importWorkbookFromFile, saveWorkbook, updateWorkbookFromFile } from 'cadenza/api-client/workbook-api';
import type { SaveWorkbookResult } from 'cadenza/api-client/workbook-api-util';
import { type WorkOrderId, deleteWorkOrders, observeForResult } from 'cadenza/api-client/workorder-api';
import type { DataProtectionContextId } from 'cadenza/repository/model/data-protection-context-id.gen';
import type { RepositoryName } from 'cadenza/repository/model/repository-name.gen';
import type { WorkbookId } from 'cadenza/repository/model/workbook-id.gen';
import type { ImportWorkbookDto } from 'cadenza/workbook/model/import-workbook-dto';

import i18n from './save-workbook-actions.properties';

const COMPONENT_NAME = 'd-save-workbook-progress-modal';
const DELAY_TIME = 500;

export class SaveWorkbookProgressModal extends Modal<boolean> {
  #workOrderId?: WorkOrderId;
  #progressBar: ProgressBar;

  constructor() {
    super({
      title: i18n('save-workbook.progress'),
      titleIcon: icon(importIcon),
    });
    this.#progressBar = new ProgressBar();
    this.#progressBar.label = i18n('save-workbook.progress');
    this.addCancelButton();
  }

  override connectedCallback() {
    super.connectedCallback();
    this.dialog.classList.add(COMPONENT_NAME);
    this.body.append(this.#progressBar);
  }

  override cancel() {
    this._cancelExport();
  }

  async saveWorkbookWithProgress(repositoryName: RepositoryName, saveWorkbookDto: SaveWorkbookDto) {
    this.#workOrderId = await saveWorkbook(repositoryName, saveWorkbookDto);
    const result = await delay(this._waitForResult(this.#workOrderId));
    this.close();
    return result;
  }

  async importWorkbookWithProgress(repositoryName: RepositoryName, importWorkbookDto: ImportWorkbookDto) {
    this.#workOrderId = await importWorkbookFromFile(repositoryName, importWorkbookDto);
    const result = await delay(this._waitForResult(this.#workOrderId));
    this.close();
    return result;
  }

  async reimportWorkbookWithProgress(
    workbookId: WorkbookId,
    name: string,
    { fileName, signature }: UploadedFileDto,
    dataProtectionContextId?: DataProtectionContextId,
  ) {
    this.#workOrderId = await updateWorkbookFromFile(
      workbookId,
      name,
      {
        fileName,
        signature,
      },
      dataProtectionContextId,
    );
    const result = await delay(this._waitForResult(this.#workOrderId));
    this.close();
    return result;
  }

  _waitForResult(workOrderId: WorkOrderId) {
    return observeForResult<unknown, WorkOrderProgressEvent, SaveWorkbookResult>(workOrderId, SAVE_WORKBOOK_RESULT)
      .progress(({ progress }) => {
        this._updateProgress(progress);
      })
      .then((result) => {
        return result;
      })
      .catch((error) => {
        setTimeout(() => this.close(), DELAY_TIME);
        throw error;
      });
  }

  _cancelExport() {
    assert(this.#workOrderId, 'No workOrderId has been provided!');
    deleteWorkOrders([this.#workOrderId])
      .then(() => super.close())
      .catch((error) => {
        logger.error('Failed to cancel import', error);
      });
  }

  _updateProgress(value: number) {
    this.#progressBar.value = value;
  }
}

const SAVE_WORKBOOK_RESULT = 'workbook-save-result';

customElements.define(COMPONENT_NAME, SaveWorkbookProgressModal);
